import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// In-memory token — set by auth.jsx login(). Provides a Bearer fallback for the
// current SPA session in case the browser refuses/loses the httpOnly cookie
// (some privacy settings block SameSite=Strict on subresource requests).
// Never persisted: on a full page refresh the app falls back to the cookie via
// /auth/me. Best of both worlds: security + reliability.
let inMemoryToken = null;
export const setInMemoryToken = (t) => { inMemoryToken = t || null; };

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (inMemoryToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${inMemoryToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Public pages (landing + login flow itself) must NEVER be bumped by a
    // 401 — the landing calls no auth endpoints directly but the AuthProvider
    // probes /auth/me on mount, which legitimately 401s for anonymous users.
    const pathname = window.location.pathname;
    const isPublic = pathname.startsWith("/login") || pathname.startsWith("/landing");
    if (err?.response?.status === 401 && !isPublic) {
      // Session cookie invalidated (tab reopen, JWT expired, server logout, ...)
      // Purge our sessionStorage cache and force a full re-auth flow.
      sessionStorage.removeItem("sentinel_user");
      inMemoryToken = null;
      window.location.href = "/landing";
    }
    return Promise.reject(err);
  }
);

/**
 * Fetch helper that always includes the auth cookie AND the in-memory Bearer
 * (for blob/stream endpoints where axios blob handling is verbose).
 */
export const authedFetch = (url, opts = {}) => {
  const headers = { ...(opts.headers || {}) };
  if (inMemoryToken) headers.Authorization = `Bearer ${inMemoryToken}`;
  return fetch(url, { ...opts, headers, credentials: "include" });
};
